import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CanAccess, RecordContextProvider } from 'react-admin';
import { useGetOne, useGetList, useTranslate } from 'ra-core';
import { CheckCircle, Ban, RefreshCw } from 'lucide-react';
import {
  CurrencyDollarIcon,
  WarningIcon,
  CrosshairIcon,
  TargetIcon,
  ImagesSquareIcon,
  ClipboardTextIcon,
  LinkSimpleIcon,
  FileArrowDownIcon,
  ClockCounterClockwiseIcon,
  ChatCircleDotsIcon,
  ArrowCircleLeftIcon,
} from '@phosphor-icons/react';

import { Offer } from '../types';
import NotFound from '../../../NotFound';
import type { Creative } from '../offerDrawer/creatives/types';
import { DEFAULT_DATE_FIELD_OPTIONS } from '../../../constants';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SectionCard } from '@/components/ui/section-card';
import { Reveal } from '@/components/motion/Reveal';
import { Breadcrumb, HeaderActions } from '@/components/admin';
import { ScrollPill } from '@/components/ui/scroll-pill';

import Pricing from '../offerDrawer/Pricing';
import ContentWrapper from '../offerDrawer/ContentWrapper';
import LabeledHorizontal from '../../../components/raExtend/labels/LabeledHorizontal';
import ListField from '../../../components/raExtend/fields/ListField';
import OneLineTextField from '../../../components/raExtend/fields/OneLineTextField';
import PurchaseOrderCard from '../offerDrawer/PurchaseOrderCard';
import NegotiationCard from '../negotiation/NegotiationCard';
import NegotiationHistory from '../negotiation/NegotiationHistory';
import LegacyCreatives from '../offerDrawer/creatives/LegacyCreatives';
import PreferredCopies from '../offerDrawer/PreferredCopies';
import TrackingLinks from '../offerDrawer/creatives/TrackingLinks';
import AcceptOfferModal from '../negotiationModals/AcceptOfferModal';
import DeclineOfferModal from '../negotiationModals/DeclineOfferModal';
import CounterOfferModal from '../negotiationModals/CounterOfferModal';
import BaseDrawer from '../offerDrawer/BaseDrawer';
import SuccessButton from '../../../components/ui/SuccesButton';
import ErrorButton from '../../../components/ui/ErrorButton';
import SecondaryButton from '../../../components/ui/SecondaryButton';

import {
  OfferCreativeDrawerProvider,
  useOfferCreativeDrawer,
} from './OfferCreativeDrawerContext';
import OfferCreativeDrawer from './OfferCreativeDrawer';
import OfferCreativeCard from './OfferCreativeCard';
import { OfferProgress } from './OfferProgress';
import { OfferStatusAlert } from './OfferStatusAlert';

// Map offer tab_filter to the new two-tab URL scheme
function getTabForOffer(tabFilter: string): string {
  return tabFilter === 'ended' ? 'finished' : 'active';
}

function formatDate(value: string | undefined): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en', DEFAULT_DATE_FIELD_OPTIONS).format(
    new Date(value)
  );
}

const hasAnyTargeting = (tg?: Offer['targeting']): boolean =>
  !!tg && Object.values(tg).some(v => Array.isArray(v) && v.length > 0);

function TargetingRow({ label, values }: { label: string; values?: string[] }) {
  if (!values || values.length === 0) return null;
  return (
    <LabeledHorizontal label={label}>
      <span className="text-sm text-muted-foreground">{values.join(', ')}</span>
    </LabeledHorizontal>
  );
}

function OfferShowSkeleton() {
  return (
    <div className="flex flex-col flex-1 min-h-0">
      <ScrollPill className="flex-1">
        <div className="p-6 lg:p-10 flex flex-col gap-8 animate-in fade-in-0 duration-300">
          {/* Offer header */}
          <div className="flex items-center gap-3">
            <Skeleton className="size-[60px] rounded-xl shrink-0" />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-3.5 w-24" />
              <Skeleton className="h-6 w-2/5" />
              <Skeleton className="h-5 w-16 rounded-full mt-1" />
            </div>
          </div>
          {/* Pricing card */}
          <Skeleton className="h-[130px] rounded-2xl" />
          {/* Dates card */}
          <Skeleton className="h-[100px] rounded-2xl" />
          {/* Another card */}
          <Skeleton className="h-[90px] rounded-2xl" />
        </div>
      </ScrollPill>
    </div>
  );
}

function CreativesSection({ offerId }: { offerId: string }) {
  const { data: creatives = [], isLoading } = useGetList<Creative>(
    `offers/${offerId}/creatives`
  );
  const { openDrawer } = useOfferCreativeDrawer();
  const t = useTranslate();

  if (!isLoading && !creatives.length) return null;

  return (
    <SectionCard
      icon={<ImagesSquareIcon />}
      title={t('common.text.creatives')}
      subtitle="Creative assets for this offer"
      bodyClassName="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
    >
      {isLoading
        ? Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[1.89/1] rounded-xl" />
          ))
        : creatives.map(creative => (
            <OfferCreativeCard
              key={creative.id}
              creative={creative}
              onViewDetails={() => openDrawer(creative)}
            />
          ))}
    </SectionCard>
  );
}

function OfferShowContent() {
  const { publisherId, offerId } = useParams<{
    publisherId: string;
    offerId: string;
  }>();

  const {
    data: offer,
    isPending,
    refetch: refetchOffer,
  } = useGetOne<Offer>('offers', { id: offerId! });

  const t = useTranslate();
  const [isAcceptOpen, setAcceptOpen] = useState(false);
  const [isDeclineOpen, setDeclineOpen] = useState(false);
  const [isCounterOpen, setCounterOpen] = useState(false);
  const [isNegotiationHistoryOpen, setIsNegotiationHistoryOpen] =
    useState(false);
  const [isAccepted, setIsAccepted] = useState(false);

  const handleOpenCounterFromDecline = () => {
    setDeclineOpen(false);
    setCounterOpen(true);
  };

  const backTab = getTabForOffer(offer?.tab_filter ?? 'pending');
  const backPath = `/publisher/${publisherId}/offers/${backTab}`;
  const backLabel = 'My Offers';

  const canNegotiate =
    offer?.status === 'reviewing' || offer?.status === 'new-counter';
  const isAcceptedOrActive =
    offer?.status === 'accepted' || offer?.status === 'active';

  return (
    <div className="flex flex-col h-full gap-2">
      {/* Breadcrumb — stable across load states; offer name fills in when ready */}
      <Breadcrumb>
        <Breadcrumb.Item>
          <Breadcrumb.Link asChild>
            <Link to={backPath}>{backLabel}</Link>
          </Breadcrumb.Link>
        </Breadcrumb.Item>
        {offer && (
          <Breadcrumb.Item className="gap-3">
            {offer.po_number && (
              <Breadcrumb.Badge>{offer.po_number}</Breadcrumb.Badge>
            )}
            <Breadcrumb.PageItem>{offer.name}</Breadcrumb.PageItem>
          </Breadcrumb.Item>
        )}
      </Breadcrumb>

      {/* Back button in header — only when loaded */}
      {!isPending && offer && (
        <HeaderActions>
          <Button variant="secondary" size="lg" asChild>
            <Link to={backPath}>
              <ArrowCircleLeftIcon className="size-5" />
              Back to My Offers
            </Link>
          </Button>
        </HeaderActions>
      )}

      {isPending ? (
        <OfferShowSkeleton />
      ) : !offer || offer.status === 'voided' ? (
        <NotFound />
      ) : (
        <RecordContextProvider value={offer}>
          <div className="flex flex-col flex-1 min-h-0">
            <ScrollPill className="flex-1">
              <div className="p-6 lg:p-10 flex flex-col gap-8">
                {/* ── Offer header ─────────────────────────────────── */}
                <Reveal index={0}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className="shrink-0">
                        {offer.brand?.logo ? (
                          <img
                            src={offer.brand.logo}
                            alt={offer.brand.name}
                            className="size-[60px] rounded-xl border border-border object-contain shadow-elev-1"
                          />
                        ) : (
                          <div className="flex size-[60px] items-center justify-center rounded-xl border border-border bg-muted text-xl font-bold text-muted-foreground shadow-elev-1">
                            {offer.brand?.name?.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-medium text-muted-foreground leading-4">
                          {offer.brand?.name}
                        </span>
                        <h1 className="text-xl font-semibold leading-tight tracking-tight">
                          {offer.name}
                        </h1>
                        {(offer.po_number || offer.io_number) && (
                          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                            {/* PO is the primary identifier; IO stays visible
                                on the offer page as a secondary reference. */}
                            {offer.po_number && (
                              <span className="w-fit px-2 py-0.5 text-xs font-medium bg-secondary border border-border rounded-sm font-tabular">
                                PO {offer.po_number}
                              </span>
                            )}
                            {offer.io_number && (
                              <span className="w-fit px-2 py-0.5 text-xs font-medium text-muted-foreground border border-border rounded-sm font-tabular">
                                IO {offer.io_number}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <OfferStatusAlert status={offer.status} />
                  </div>
                </Reveal>

                {/* ── Progress ──────────────────────────────────────── */}
                <Reveal index={1}>
                  <OfferProgress
                    status={offer.status}
                    endDate={offer.end_date}
                  />
                </Reveal>

                {/* ── Action Required ───────────────────────────────── */}
                <CanAccess action="edit" resource="offer-negotiations">
                  {canNegotiate && (
                    <Reveal index={1}>
                      <SectionCard
                        icon={<ChatCircleDotsIcon />}
                        title={t('common.text.action_required', {
                          _: 'Action Required',
                        })}
                        subtitle="Review and respond to this offer"
                      >
                        <div className="flex flex-col gap-4">
                          {offer.status === 'new-counter' &&
                            offer.last_negotiation && (
                              <NegotiationCard
                                negotiation={offer.last_negotiation}
                              />
                            )}
                          <div className="flex flex-wrap gap-3">
                            <SuccessButton onClick={() => setAcceptOpen(true)}>
                              <CheckCircle className="size-4" />
                              {t('offer.drawer.button.accept')}
                            </SuccessButton>
                            <ErrorButton onClick={() => setDeclineOpen(true)}>
                              <Ban className="size-4" />
                              {t('offer.drawer.button.decline')}
                            </ErrorButton>
                            <SecondaryButton
                              onClick={() => setCounterOpen(true)}
                            >
                              <RefreshCw className="size-4" />
                              {t('offer.drawer.button.counter')}
                            </SecondaryButton>
                          </div>
                        </div>
                      </SectionCard>
                    </Reveal>
                  )}
                </CanAccess>

                {/* ── Creatives ─────────────────────────────────────── */}
                {Boolean(offer.creatives?.length) && (
                  <Reveal index={2}>
                    <SectionCard
                      icon={<ImagesSquareIcon />}
                      title={t('common.text.creatives')}
                      subtitle="Creative assets"
                    >
                      <LegacyCreatives creatives={offer.creatives} />
                    </SectionCard>
                  </Reveal>
                )}

                {Boolean(offer.offer_creatives?.length) && (
                  <Reveal index={2}>
                    <CreativesSection offerId={offer.id} />
                  </Reveal>
                )}

                {/* ── Pricing and Terms ─────────────────────────────── */}
                <Reveal index={3}>
                  <SectionCard
                    icon={<CurrencyDollarIcon />}
                    title="Pricing and Terms"
                    subtitle="Budget, rate & campaign dates"
                  >
                    <div className="flex flex-col gap-4">
                      {(offer.status === 'counter-in-review' ||
                        offer.status === 'new-counter') && (
                        <div className="flex items-start gap-2.5 rounded-lg border border-amber/30 bg-amber/10 px-3.5 py-3">
                          <WarningIcon
                            weight="fill"
                            className="size-4 text-amber shrink-0 mt-px"
                          />
                          <p className="text-xs font-medium text-amber leading-relaxed">
                            These terms may change once the current negotiation
                            concludes.
                          </p>
                        </div>
                      )}
                      <Pricing />
                      <div className="border-t border-border" />
                      <ContentWrapper>
                        <LabeledHorizontal label={t('common.text.start')}>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(offer.start_date)}
                          </span>
                        </LabeledHorizontal>
                        <LabeledHorizontal label={t('common.text.end')}>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(offer.end_date)}
                          </span>
                        </LabeledHorizontal>
                      </ContentWrapper>
                    </div>
                  </SectionCard>
                </Reveal>

                {/* ── Negotiations (pending offers) ─────────────────── */}
                {!isAcceptedOrActive && offer.last_negotiation && (
                  <Reveal index={4}>
                    <SectionCard
                      icon={<ClockCounterClockwiseIcon />}
                      title={t('common.text.negotiations')}
                      subtitle="Current offer terms"
                      action={
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setIsNegotiationHistoryOpen(true)}
                          className="text-xs"
                        >
                          {t('common.text.view_history')}
                        </Button>
                      }
                    >
                      <NegotiationCard negotiation={offer.last_negotiation} />
                    </SectionCard>
                  </Reveal>
                )}

                {/* ── Targeting (from the campaign) ─────────────────── */}
                {hasAnyTargeting(offer.targeting) && (
                  <Reveal index={6}>
                    <SectionCard
                      icon={<CrosshairIcon />}
                      title={t('common.text.targeting')}
                      subtitle="Campaign audience & geo"
                    >
                      <ContentWrapper>
                        <TargetingRow
                          label="Markets"
                          values={offer.targeting?.markets}
                        />
                        <TargetingRow
                          label="Exclude markets"
                          values={offer.targeting?.exclude_markets}
                        />
                        <TargetingRow
                          label="Regions"
                          values={offer.targeting?.regions}
                        />
                        <TargetingRow
                          label="Exclude regions"
                          values={offer.targeting?.exclude_regions}
                        />
                        <TargetingRow
                          label="Audience languages"
                          values={offer.targeting?.audience_languages}
                        />
                        <TargetingRow
                          label="Age range"
                          values={offer.targeting?.age_ranges}
                        />
                        <TargetingRow
                          label="Gender"
                          values={offer.targeting?.genders}
                        />
                        <TargetingRow
                          label="Household income"
                          values={offer.targeting?.household_incomes}
                        />
                        <TargetingRow
                          label="Buyer type"
                          values={offer.targeting?.buyer_types}
                        />
                        <TargetingRow
                          label="Interests"
                          values={offer.targeting?.interests}
                        />
                      </ContentWrapper>
                    </SectionCard>
                  </Reveal>
                )}

                {/* ── Goals ─────────────────────────────────────────── */}
                {offer.goals && (
                  <Reveal index={7}>
                    <SectionCard
                      icon={<TargetIcon />}
                      title={t('common.text.goals')}
                      subtitle="Campaign objectives"
                    >
                      <ContentWrapper>
                        <ListField<Offer> source="goals" />
                      </ContentWrapper>
                    </SectionCard>
                  </Reveal>
                )}

                {/* ── Remaining accepted / active sections ──────────── */}
                {isAcceptedOrActive && (
                  <>
                    {Boolean(offer.preferred_copies?.length) && (
                      <Reveal index={8}>
                        <SectionCard
                          icon={<ClipboardTextIcon />}
                          title={t('common.text.preferred_copy')}
                          subtitle="Suggested copy"
                        >
                          <PreferredCopies
                            preferredCopies={offer.preferred_copies}
                          />
                        </SectionCard>
                      </Reveal>
                    )}

                    {Boolean(offer.promo_code) && (
                      <Reveal index={9}>
                        <SectionCard
                          icon={<ClipboardTextIcon />}
                          title={t('common.text.promo_code')}
                          subtitle="Promotional code"
                        >
                          <ContentWrapper>
                            <OneLineTextField<Offer>
                              label={t('common.text.promo_code')}
                              source="promo_code"
                            />
                          </ContentWrapper>
                        </SectionCard>
                      </Reveal>
                    )}

                    {!offer.offer_creatives?.length && offer.trackinglinks && (
                      <Reveal index={9}>
                        <SectionCard
                          icon={<LinkSimpleIcon />}
                          title="Tracking Links"
                          subtitle="Affiliate tracking"
                        >
                          <TrackingLinks trackingLinks={offer.trackinglinks} />
                        </SectionCard>
                      </Reveal>
                    )}

                    {offer.last_negotiation && (
                      <Reveal index={10}>
                        <SectionCard
                          icon={<ClockCounterClockwiseIcon />}
                          title={t('common.text.negotiations')}
                          subtitle="Current terms"
                          action={
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setIsNegotiationHistoryOpen(true)}
                              className="text-xs"
                            >
                              {t('common.text.view_history')}
                            </Button>
                          }
                        >
                          <NegotiationCard
                            negotiation={offer.last_negotiation}
                          />
                        </SectionCard>
                      </Reveal>
                    )}
                  </>
                )}

                {/* ── Purchase order (+ revision addendums) ─────────── */}
                {Boolean(offer.po_url) && (
                  <Reveal index={11}>
                    <SectionCard
                      icon={<FileArrowDownIcon />}
                      title={t('common.text.purchase_order')}
                      subtitle={
                        offer.revisions?.length
                          ? 'Purchase order & addendums'
                          : 'Purchase order document'
                      }
                      bodyClassName="flex flex-col gap-4"
                    >
                      <PurchaseOrderCard
                        url={offer.po_url}
                        fileName="purchase-order.pdf"
                      />
                      {offer.revisions?.map(revision => (
                        <PurchaseOrderCard
                          key={revision.id}
                          url={revision.download_url}
                          fileName={`${revision.addendum_label} (addendum).pdf`}
                        />
                      ))}
                    </SectionCard>
                  </Reveal>
                )}
              </div>
            </ScrollPill>
          </div>

          {/* ── Negotiation modals ─────────────────────────────── */}
          <CanAccess action="edit" resource="offer-negotiations">
            {isAcceptOpen && (
              <AcceptOfferModal
                offer={offer}
                open={isAcceptOpen}
                onClose={() => setAcceptOpen(false)}
                refetchOffer={refetchOffer}
                isAccepted={isAccepted}
                setIsAccepted={setIsAccepted}
              />
            )}
            {isDeclineOpen && (
              <DeclineOfferModal
                offer={offer}
                open={isDeclineOpen}
                onClose={() => setDeclineOpen(false)}
                onCounter={handleOpenCounterFromDecline}
              />
            )}
            {isCounterOpen && (
              <CounterOfferModal
                offer={offer}
                open={isCounterOpen}
                onClose={() => setCounterOpen(false)}
              />
            )}
          </CanAccess>

          {/* ── Negotiation history drawer ─────────────────────── */}
          <BaseDrawer
            open={isNegotiationHistoryOpen}
            onClose={() => setIsNegotiationHistoryOpen(false)}
            className="max-w-xl"
          >
            <NegotiationHistory
              onClose={() => setIsNegotiationHistoryOpen(false)}
            />
          </BaseDrawer>
        </RecordContextProvider>
      )}

      <OfferCreativeDrawer
        isOfferAccepted={isAcceptedOrActive}
        brandLogo={offer?.brand?.logo}
        brandName={offer?.brand?.name}
      />
    </div>
  );
}

export function OfferShow() {
  return (
    <OfferCreativeDrawerProvider>
      <OfferShowContent />
    </OfferCreativeDrawerProvider>
  );
}
